import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject } from '@angular/core';
import anime from 'animejs/lib/anime.es.js';
import {BrowserService} from '../../generic/service/browser.service';
import {ProductService} from '../../generic/service/product.service';
import {NgStyle} from '@angular/common';

/**
 * Add to shoppingcart animation component. This component listens for a event from the buy unit buttons.
 * Whenever a product is added to the shopping cart this component runs an animation for that product by creating
 * an animatable element that animates from the product row image position to the shoppingcart header button.
 *
 * @author Patrick van Zadel
 */
@Component({
    selector: 'add-to-shoppingcart-animation',
    templateUrl: './add.to.shoppingcart.animation.component.html',
    styleUrls: ['./add.to.shoppingcart.animation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgStyle],
})
export class AddToShoppingcartAnimationComponent {
    private productService = inject(ProductService);
    private changeDetector = inject(ChangeDetectorRef);
    private element = inject(ElementRef);

    public image: string;
    public imageWidth: number = 0;
    public imageHeight: number = 0;
    private elementId: string;
    private thumbnailImageFormat: number = 182;

    constructor() {
        const productService = this.productService;

        productService.productAdded$.subscribe(product => {
            if (BrowserService.isEdgeBrowser()) {
                return;
            }

            this.image = product.image;
            this.elementId = this.productService.getAnchorValue(
                product.type,
                product.priceListNo,
                product.priceListLineNo,
            );
	        this.changeDetector.markForCheck();
            this.addToCartAnimation();
        });
    }

    /**
     * Get the element to animate and the coordinates to animate on and start a second long animation.
     *
     * @param element
     * @param coordinates
     */
    private static cartAnimation(element, coordinates) {
        const startPos = AddToShoppingcartAnimationComponent.getCoordinates(
            coordinates.start.x,
            coordinates.start.y,
        );
        const endPos = AddToShoppingcartAnimationComponent.getCoordinates(
            coordinates.final.x,
            coordinates.final.y,
        );

        // Don't animate the image if it is behind the header, or if the users scrolled the page far enough
        if (coordinates.start.y <= 0) {
            return;
        }

        anime({
            targets: element,
            translateX: [
                { value: startPos.x, duration: 0, delay: 0 },
                { value: endPos.x, duration: 700, delay: 0 },
            ],
            translateY: [
                { value: startPos.y, duration: 0, delay: 0 },
                { value: endPos.y, duration: 700, delay: 0 },
            ],
            translateZ: [
                { value: 0, duration: 0, delay: 0 },
                { value: 0, duration: 700, delay: 0 },
            ],
            scale: [
                { value: 1, duration: 0, delay: 300 },
                { value: 0, duration: 400, delay: 0 },
            ],
            opacity: [
                { value: 1, duration: 0, delay: 300 },
                { value: 0, duration: 400, delay: 0 },
            ],
            easing: 'linear',
        });
    }

    /**
     * Get the coordinates for the starting element and the finishing element. These coordinates are used
     * to animate the object on.
     *
     * @param startingElement
     * @param finalElement
     */
    private static getAnimationCoordinates(startingElement, finalElement) {
        const start = startingElement.getBoundingClientRect();
        const final = finalElement.getBoundingClientRect();

        return {
            start: {
                x: start.left,
                y: start.top,
            },
            final: {
                x: final.left,
                y: final.top,
            },
        };
    }

    /**
     * This function is used for adding extra x and y values for the animation to animate on.
     *
     * @param x
     * @param y
     */
    private static getCoordinates(x, y) {
        if (!x) {
            const x = 0;
        }

        if (!y) {
            const y = 0;
        }

        return { x, y };
    }

    /**
     * Check if the product row exists.
     *
     * @param elementId
     */
    private static getProductElementById(elementId: string): any {
        const productRowElement = document.getElementById(elementId);
        return productRowElement !== null ? productRowElement : false;
    }

    /**
     * Check if the product image exists for the given row.
     *
     * @param productElement
     */
    private static getProductImageForRow(productElement: any): any {
        const productRowElementImage = productElement.querySelector(
            '.overview-product_image img',
        );
        return productRowElementImage ? productRowElementImage : false;
    }

    getProductImage(): string {
        const imageId = this.image.split('.').slice(0, -1).join('.');
        return `https://img.noviflora.com/images/${imageId}/${this.thumbnailImageFormat}.jpg`;
    }

    /**
     * Add to Cart animation starting point.
     */
    addToCartAnimation(): void {
        const nativeElement = this.element.nativeElement;
        const element = nativeElement.querySelector(
            '.product-add-to-cart-animation-object',
        );

        const productRowElement =
            AddToShoppingcartAnimationComponent.getProductElementById(
                this.elementId,
            );

        if (productRowElement) {
            const productRowElementImage =
                AddToShoppingcartAnimationComponent.getProductImageForRow(
                    productRowElement,
                );

            if (productRowElementImage) {
                const productImageSrc =
                    productRowElementImage.getAttribute('src');

                if (
                    productImageSrc === null ||
                    productImageSrc.includes('fallback-product')
                ) {
                    return;
                }

                const shoppingCartHeaderElement = document.querySelector(
                    '.header-shoppingcart-button',
                );
                const coordinates =
                    AddToShoppingcartAnimationComponent.getAnimationCoordinates(
                        productRowElementImage,
                        shoppingCartHeaderElement,
                    );

                this.imageWidth =
                    productRowElementImage.getBoundingClientRect().width;
                this.imageHeight =
                    productRowElementImage.getBoundingClientRect().height;
                this.changeDetector.markForCheck();

                return AddToShoppingcartAnimationComponent.cartAnimation(
                    element,
                    coordinates,
                );
            }
        }
    }
}
